/**
 * Shared JSON request helper for the expansion-pack surfaces (Forms,
 * Proposals, Products, room CRM panel — 2026-09-07).
 *
 * The rest of the app declares API_BASE per file and hand-rolls fetch
 * + error parsing in each component. That was fine at one or two
 * fetches per file; the proposal editor alone makes nine distinct
 * calls, so this file owns the pattern once. Same base URL fallback
 * as every other component, so a missing REACT_APP_API_URL still
 * reaches production instead of the SPA's own domain.
 *
 * Errors carry the backend's `code` (e.g. 'loss_reason_required',
 * 'crm_not_connected') and the HTTP status so callers can branch on
 * them instead of string-matching messages.
 */

export const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

export async function apiRequest(path, { token = null, method = 'GET', body = undefined, headers = {} } = {}) {
  const init = {
    method,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const r = await fetch(`${API_BASE}${path}`, init);
  if (r.status === 204) return null;
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(data.error || `Request failed (${r.status})`);
    err.code = data.code || null;
    err.status = r.status;
    throw err;
  }
  return data;
}

/** Cents → "$1,234.56". Null/undefined → em dash. */
export function moneyLabel(cents, currency = 'USD') {
  if (cents === null || cents === undefined || Number.isNaN(Number(cents))) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(cents) / 100);
  } catch (_) {
    return `$${(Number(cents) / 100).toFixed(2)}`;
  }
}

/** "$1,234.56" or "1234.56" → integer cents. Invalid → NaN. */
export function parseMoneyToCents(input) {
  const s = String(input ?? '').replace(/[^0-9.-]/g, '');
  if (!s) return 0;
  const n = Number(s);
  if (!Number.isFinite(n)) return NaN;
  return Math.round(n * 100);
}

export function formatWhen(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
